/**
 * citationNavigation — the single decision point for what an AskAICA citation
 * click should do. Pure resolver: no hooks, no DOM/window access — callers
 * (useCitationActions) execute the returned action. Pattern:
 * common/NotificationCenter/notificationNavigation.ts.
 */

import { CitationSource } from '../../../../../../../../store/dashboard/askAica/citationTypes';
import { REPORT_TABS, REPORT_TAB_STATES } from '../../../../../../components/DashboardHeader/TabConstants';
import { portfolioDetailPath, companyPortfolioDetailPath } from '../../../../../../../../utils/constants/routesConst';
import { CITATION_SOURCE_TYPE, ALLOWED_REPORT_PARAM_KEYS } from './MessagesContainer.constants';

export interface DashboardConfigTab {
    // Widened to match the real Redux `Tab` shape (common/TabSelector/TabSelector.tsx) —
    // this resolver only reads `tabs`, it never constructs one.
    id?: string;
    label?: string;
    showInSidebar?: boolean;
}

export interface DashboardConfigCompanyTab {
    id: string;
    state?: string;
}

/** Minimal shape of Polus `dashboardConfig` (§4.3) this resolver reads. */
export interface CitationDashboardConfig {
    tabs?: DashboardConfigTab[];
    additionalDetailsConfig?: {
        companyTabs?: DashboardConfigCompanyTab[];
    };
}

export interface CitationNavigationContext {
    /** Route `:id` param ONLY — undefined when the current route has no org id
     *  (e.g. /company/deals). Never fall back to userData.investorOrgId (SF-3):
     *  that is the PNO org, not the report's org. */
    orgId?: string;
    /** true for COMPANY/borrower sessions — the SINGLE criterion is
     *  `userData.accountType === USER_TYPE.COMPANY`, never the current pathname. */
    isCompanyAccount: boolean;
    /** Live Polus dashboardConfig; null/undefined while not yet hydrated. */
    dashboardConfig?: CitationDashboardConfig | null;
}

export type CitationAction =
    | { kind: 'document'; docId: string; fileName: string }
    | { kind: 'section'; url: string; reportSection: string; sectionLabel?: string }
    | { kind: 'conversation'; conversationId: string; targetMessageId: string | null }
    | {
          kind: 'scroll';
          reportSection?: string;
          fallbackReason?: 'section_absent' | 'locked' | 'config_unhydrated' | 'window_blocked';
      };

const isAicaReportUnlocked = (dashboardConfig: CitationDashboardConfig): boolean =>
    dashboardConfig.additionalDetailsConfig?.companyTabs?.find((tab) => tab.id === REPORT_TABS.AICA_REPORT)?.state ===
    REPORT_TAB_STATES.UNLOCKED;

const buildSectionUrl = (ctx: CitationNavigationContext, reportSection: string, params?: Record<string, string>): string => {
    const orgId = ctx.orgId as string;
    const basePath = ctx.isCompanyAccount ? companyPortfolioDetailPath(orgId) : portfolioDetailPath(orgId);
    const query = new URLSearchParams();
    query.set('tab', REPORT_TABS.AICA_REPORT);
    query.set('selectedTab', reportSection);
    ALLOWED_REPORT_PARAM_KEYS.forEach((key) => {
        const value = params?.[key];
        if (value) query.set(key, value);
    });
    return `${basePath}?${query.toString()}`;
};

/**
 * document — OPTIONAL-2 guard: `doc_id` absent/"" (Athena's `_align_document_metadata`
 * no-match sentinel) falls back to scroll rather than requesting `fileId=0`
 * (`Number('') === 0`).
 *
 * tool — navigates only when a route org id is present, the live
 * `dashboardConfig` is hydrated, the section exists in `tabs`, AND the top-level
 * `aicaReport` entry in `additionalDetailsConfig.companyTabs` is exactly `UNLOCKED`.
 * Every other combination falls back to scroll — `showInSidebar:false` sections
 * still navigate (only tab membership is checked). A tool source that DID carry a
 * `report_section` but fell back to scroll tags the descriptor with `reportSection`
 * + a `fallbackReason` (`section_absent` / `locked` / `config_unhydrated`) so the
 * caller can log why — a source with NO `report_section` at all stays a bare
 * `{kind:'scroll'}`, same as today (the 'unmapped' case never needs a reason).
 *
 * Everything else (`Web`/`Calculation`/`Internal Tool`/`model_knowledge`) falls back
 * to scroll — the universal fallback.
 */
export function resolveCitationAction(source: CitationSource, ctx: CitationNavigationContext): CitationAction {
    if (source.type === CITATION_SOURCE_TYPE.DOCUMENT) {
        if (!source.doc_id) return { kind: 'scroll' };
        return { kind: 'document', docId: source.doc_id, fileName: source.file_name };
    }

    if (source.type === CITATION_SOURCE_TYPE.CONVERSATION_HISTORY) {
        // Cross-chat (org-memory recall from a DIFFERENT conversation): navigate
        // there via the same switch-conversation mechanism ConversationSidebar's
        // row click uses — reusing the existing panel rather than a separate
        // viewer. Same-chat (no source_conversation_id) or a memo with no single
        // origin message (both ids null) has nothing to navigate to — falls back
        // to the universal scroll — not built out to a specific-message anchor yet.
        if (source.source_conversation_id) {
            return { kind: 'conversation', conversationId: source.source_conversation_id, targetMessageId: source.source_message_id };
        }
        return { kind: 'scroll' };
    }

    if (source.type !== CITATION_SOURCE_TYPE.TOOL) return { kind: 'scroll' };

    const reportSection = source.report_section;
    if (!reportSection) return { kind: 'scroll' };
    if (!ctx.orgId || !ctx.dashboardConfig) return { kind: 'scroll', reportSection, fallbackReason: 'config_unhydrated' };

    const tab = ctx.dashboardConfig.tabs?.find((t) => t.id === reportSection);
    if (!tab) return { kind: 'scroll', reportSection, fallbackReason: 'section_absent' };
    if (!isAicaReportUnlocked(ctx.dashboardConfig)) return { kind: 'scroll', reportSection, fallbackReason: 'locked' };

    return {
        kind: 'section',
        url: buildSectionUrl(ctx, reportSection, source.report_section_params),
        reportSection,
        ...(tab.label ? { sectionLabel: tab.label } : {}),
    };
}
